import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useSEO from '../hooks/useSEO';
import { SubscriptionTier, useSubscriptionTiers } from '../hooks/useSubscriptionTiers';
import BundlePreparationOverlay from '../components/BundlePreparationOverlay';
import { ROUTES } from '../constants/routes';
import { GiftOrder } from '../types';

interface SubscriptionSelectionPageProps {
  onSelectTier: (metadata: GiftOrder['subscription']) => Promise<void> | void;
  onLogoClick?: () => void;
}

const getB2BTierIcon = (tier: SubscriptionTier) => {
  if (tier.isCustomTier) {
    return (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="m12 3 1.9 4.6L18.5 9.5l-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9L12 3Z" />
        <path d="M5 16.5 6 19l2.5 1-2.5 1L5 23l-1-2.5L1.5 19l2.5-1L5 16.5Z" />
      </svg>
    );
  }

  const label = tier.label.toLowerCase();

  if (label.includes('starter')) {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
        <path d="m3.3 7 8.7 5 8.7-5" />
        <path d="M12 22V12" />
      </svg>
    );
  }

  if (label.includes('regular')) {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="m3 17 6-6 4 4 7-7" />
        <path d="M14 8h6v6" />
      </svg>
    );
  }

  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M6 7h12" />
      <path d="M8 12h8" />
      <path d="M10 17h4" />
    </svg>
  );
};

export default function SubscriptionSelectionPage({
  onSelectTier,
  onLogoClick,
}: SubscriptionSelectionPageProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const initialTab =
    new URLSearchParams(location.search).get('tab')?.toLowerCase() === 'b2b'
      ? 'b2b'
      : 'consumer';

  const [activeTab, setActiveTab] = useState<'consumer' | 'b2b'>(initialTab);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [selectingTierId, setSelectingTierId] = useState<string | null>(null);
  const { tiers: currentTiers, loading, error, refetch } = useSubscriptionTiers(activeTab);
  const isB2BTab = activeTab === 'b2b';

  useSEO({
    title: activeTab === 'b2b' ? 'Choose Your Tier - BOQBOX' : 'Choose Your Peace - BOQBOX',
    description:
      activeTab === 'b2b'
        ? 'Select a reseller subscription tier that matches your daily sales.'
        : 'Select a subscription tier that brings quiet peace to your routine.',
  });

  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab')?.toLowerCase() === 'b2b' ? 'b2b' : 'consumer';
    setActiveTab(tab);
    setSelectionError(null);
  }, [location.search]);

  const handleTabChange = (tab: 'consumer' | 'b2b') => {
    setActiveTab(tab);
    setSelectionError(null);
    navigate(`${ROUTES.TIERS}?tab=${tab}`, { replace: true });
  };

  const handleBack = () => {
    navigate(ROUTES.PATH_SELECTION);
  };

  const handleSelectTier = async (tier: SubscriptionTier) => {
    try {
      setSelectionError(null);
      setSelectingTierId(tier.id);

      await Promise.all([
        onSelectTier({
          category: activeTab,
          backendCategory: tier.backendCategory,
          tierId: tier.id,
          tierLabel: tier.label,
          frequency: tier.frequency,
          minThreshold: tier.minThreshold,
          minQuantity: tier.minQuantity,
          packageTotalPrice: tier.packageTotalPrice,
          packageItems: tier.packageItems,
        }),
        new Promise((resolve) => window.setTimeout(resolve, 500)),
      ]);
    } catch (selectionErr) {
      const message =
        selectionErr instanceof Error
          ? selectionErr.message
          : 'We could not prepare this tier right now. Please try again.';
      setSelectionError(message);
    } finally {
      setSelectingTierId(null);
    }
  };

  return (
    <div className="form-page-container">

      <div className="form-page" style={{ paddingBottom: '40px' }}>
        <div className="form-page-header">
          <button type="button" className="back-btn" onClick={handleBack} style={{ marginBottom: '16px' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          <h1 className="form-page-title" style={{ fontSize: '32px', marginBottom: '8px' }}>
            {activeTab === 'b2b' ? 'Choose Your Tier' : 'Choose Your Peace'}
          </h1>
          <p
            style={{
              color: 'var(--gray-500)',
              fontSize: '16px',
              lineHeight: '1.5',
              marginBottom: '24px',
            }}
          >
            {activeTab === 'b2b'
              ? 'Select a volume that matches your daily sales.'
              : 'Select a subscription tier that brings quiet peace to your routine.'}
          </p>
        </div>

        <div
          className="segmented-control"
          style={{
            display: 'flex',
            backgroundColor: 'var(--bx-surface-2)',
            padding: '4px',
            borderRadius: '14px',
            marginBottom: '32px',
          }}
        >
          <button
            type="button"
            onClick={() => handleTabChange('consumer')}
            disabled={selectingTierId !== null}
            style={{
              flex: 1,
              padding: '12px 0',
              borderRadius: '10px',
              border: 'none',
              fontSize: '15px',
              fontWeight: activeTab === 'consumer' ? '700' : '500',
              backgroundColor: activeTab === 'consumer' ? 'var(--bx-surface)' : 'transparent',
              color: activeTab === 'consumer' ? 'var(--black)' : 'var(--gray-500)',
              boxShadow: activeTab === 'consumer' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              cursor: selectingTierId ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              opacity: selectingTierId ? 0.7 : 1,
            }}
          >
            Consumer
          </button>
          <button
            type="button"
            onClick={() => handleTabChange('b2b')}
            disabled={selectingTierId !== null}
            style={{
              flex: 1,
              padding: '12px 0',
              borderRadius: '10px',
              border: 'none',
              fontSize: '15px',
              fontWeight: activeTab === 'b2b' ? '700' : '500',
              backgroundColor: activeTab === 'b2b' ? 'var(--bx-surface)' : 'transparent',
              color: activeTab === 'b2b' ? 'var(--black)' : 'var(--gray-500)',
              boxShadow: activeTab === 'b2b' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              cursor: selectingTierId ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              opacity: selectingTierId ? 0.7 : 1,
            }}
          >
            B2B Reseller
          </button>
        </div>

        {selectionError && (
          <div
            style={{
              border: '1.5px solid #FEE2E2',
              borderRadius: '16px',
              padding: '16px 18px',
              backgroundColor: '#FEF2F2',
              color: '#991B1B',
              marginBottom: '24px',
              fontSize: '14px',
              lineHeight: '1.5',
            }}
          >
            {selectionError}
          </div>
        )}

        <div className="tier-cards-list" style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
          {loading && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '240px',
                gap: '16px',
                color: 'var(--gray-500)',
              }}
            >
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  border: '4px solid #F3F4F6',
                  borderTop: '4px solid var(--orange)',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }}
              />
              <p style={{ margin: 0, fontSize: '15px' }}>Loading subscription tiers...</p>
            </div>
          )}

          {!loading && error && (
            <div
              style={{
                border: '1.5px solid #FEE2E2',
                borderRadius: '20px',
                padding: '24px',
                backgroundColor: '#FEF2F2',
                color: '#991B1B',
              }}
            >
              <h2 style={{ fontSize: '20px', fontWeight: '800', marginBottom: '8px' }}>
                Unable to load tiers
              </h2>
              <p style={{ fontSize: '15px', lineHeight: '1.6', marginBottom: '20px' }}>{error}</p>
              <button
                type="button"
                onClick={() => {
                  void refetch();
                }}
                style={{
                  padding: '14px 18px',
                  borderRadius: '14px',
                  border: '1.5px solid #DC2626',
                  backgroundColor: 'var(--bx-surface)',
                  color: '#DC2626',
                  fontSize: '15px',
                  fontWeight: '700',
                  cursor: 'pointer',
                }}
              >
                Try Again
              </button>
            </div>
          )}

          {!loading && !error && currentTiers.length === 0 && (
            <div
              style={{
                border: '1.5px solid #F3F4F6',
                borderRadius: '20px',
                padding: '24px',
                backgroundColor: 'var(--bx-surface)',
              }}
            >
              <h2 style={{ fontSize: '20px', fontWeight: '800', marginBottom: '8px', color: 'var(--black)' }}>
                No tiers available
              </h2>
              <p
                style={{
                  fontSize: '15px',
                  lineHeight: '1.6',
                  color: 'var(--gray-500)',
                  marginBottom: '20px',
                }}
              >
                There are no subscription tiers available for this category right now.
              </p>
              <button
                type="button"
                onClick={() => {
                  void refetch();
                }}
                style={{
                  padding: '14px 18px',
                  borderRadius: '14px',
                  border: '1.5px solid var(--orange)',
                  backgroundColor: 'var(--bx-surface)',
                  color: 'var(--orange)',
                  fontSize: '15px',
                  fontWeight: '700',
                  cursor: 'pointer',
                }}
              >
                Refresh
              </button>
            </div>
          )}

          {!loading &&
            !error &&
            currentTiers.map((tier) => (
              <div key={tier.id} className="tier-card-wrapper" style={{ position: 'relative' }}>
                {tier.isPopular && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '-14px',
                      right: isB2BTab ? '50%' : '24px',
                      transform: isB2BTab ? 'translateX(50%)' : 'none',
                      backgroundColor: '#FF6F00',
                      color: 'var(--bx-on-brand)',
                      padding: '6px 14px',
                      borderRadius: '10px',
                      fontSize: '12px',
                      fontWeight: '800',
                      letterSpacing: '0.01em',
                      zIndex: 2,
                      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    {isB2BTab ? (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                        <path d="m12 2 2.4 5.1L20 8l-4 3.9.9 5.6L12 14.8 7.1 17.5 8 11.9 4 8l5.6-.9L12 2Z" />
                      </svg>
                    ) : null}
                    Most Popular
                  </div>
                )}

                <div
                  className="tier-card"
                  style={{
                    border: tier.isPopular ? '2px solid #FF6F00' : '1.5px solid #F3F4F6',
                    borderRadius: '20px',
                    padding: '24px',
                    backgroundColor: 'var(--bx-surface)',
                    boxShadow: tier.isPopular
                      ? '0 10px 15px -3px rgba(255, 111, 0, 0.1)'
                      : '0 4px 6px -1px rgba(0,0,0,0.02)',
                  }}
                >
                  {tier.isCustomTier || isB2BTab ? (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: '16px',
                        marginBottom: '20px',
                      }}
                    >
                      <div
                        style={{
                          width: '42px',
                          height: '42px',
                          borderRadius: '14px',
                          backgroundColor: 'var(--bx-brand-soft)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'var(--orange)',
                          flexShrink: 0,
                        }}
                      >
                        {isB2BTab ? getB2BTierIcon(tier) : (
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="m12 3 1.9 4.6L18.5 9.5l-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9L12 3Z" />
                            <path d="M5 16.5 6 19l2.5 1-2.5 1L5 23l-1-2.5L1.5 19l2.5-1L5 16.5Z" />
                          </svg>
                        )}
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end',
                          flexShrink: 0,
                        }}
                      >
                        <span
                          style={{
                            fontSize: isB2BTab ? '24px' : '15px',
                            fontWeight: '800',
                            color: 'var(--orange)',
                            lineHeight: '1.1',
                            textAlign: 'right',
                          }}
                        >
                          {tier.priceRange}
                        </span>
                        <span
                          style={{
                            color: 'var(--gray-500)',
                            fontSize: '14px',
                            marginTop: '3px',
                            lineHeight: '1.2',
                            textAlign: 'right',
                          }}
                        >
                          {tier.period}
                        </span>
                      </div>
                    </div>
                  ) : null}

                  {tier.imageUrl && (
                    <div
                      style={{
                        width: '100%',
                        height: '180px',
                        borderRadius: '16px',
                        overflow: 'hidden',
                        backgroundColor: 'var(--bx-surface-2)',
                        marginBottom: '20px',
                      }}
                    >
                      <img
                        src={tier.imageUrl}
                        alt={tier.label}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                    </div>
                  )}

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: '16px',
                      marginBottom: '18px',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h2
                        style={{
                          fontSize: '20px',
                          fontWeight: '800',
                          color: 'var(--black)',
                          margin: 0,
                          lineHeight: '1.3',
                        }}
                      >
                        {tier.label}
                      </h2>

                      <p
                        style={{
                          fontSize: '15px',
                          lineHeight: '1.6',
                          color: 'var(--gray-500)',
                          margin: '10px 0 0',
                        }}
                      >
                        {tier.includes}
                      </p>
                    </div>

                    {!tier.isCustomTier && !isB2BTab ? (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end',
                          flexShrink: 0,
                        }}
                      >
                        <span
                          style={{
                            fontSize: '24px',
                            fontWeight: '800',
                            color: 'var(--orange-dark)',
                            lineHeight: '1.1',
                          }}
                        >
                          {tier.priceRange}
                        </span>
                        <span
                          style={{
                            color: 'var(--gray-500)',
                            fontSize: '14px',
                            marginTop: '4px',
                            lineHeight: '1.2',
                          }}
                        >
                          {tier.period}
                        </span>
                      </div>
                    ) : null}
                  </div>

                  <div
                    style={{
                      padding: '18px 0',
                      borderTop: '1px solid #F3F4F6',
                      marginBottom: '18px',
                    }}
                  >
                    {tier.includesList.length > 0 ? (
                      <ul
                        style={{
                          listStyle: 'none',
                          padding: 0,
                          margin: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '10px',
                        }}
                      >
                        {tier.includesList.map((includedItem) => (
                          <li
                            key={`${tier.id}-${includedItem}`}
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '10px',
                              fontSize: '15px',
                              lineHeight: '1.5',
                              color: 'var(--black)',
                            }}
                          >
                            <span
                              style={{
                                width: '6px',
                                height: '6px',
                                borderRadius: '999px',
                                backgroundColor: 'var(--orange)',
                                marginTop: '8px',
                                flexShrink: 0,
                              }}
                            />
                            <span>{includedItem}</span>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>

                  {tier.badge ? (
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 14px',
                        backgroundColor: 'var(--bx-brand-soft)',
                        color: 'var(--bx-brand-strong)',
                        borderRadius: '10px',
                        fontSize: '14px',
                        fontWeight: '600',
                        marginBottom: '24px',
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
                        <path d="m3.3 7 8.7 5 8.7-5" />
                        <path d="M12 22V12" />
                      </svg>
                      {tier.badge}
                    </div>
                  ) : null}

                  <button
                    type="button"
                    onClick={() => {
                      void handleSelectTier(tier);
                    }}
                    disabled={selectingTierId !== null}
                    style={
                      isB2BTab
                        ? {
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: 0,
                            border: 'none',
                            backgroundColor: 'transparent',
                            color: 'var(--orange)',
                            fontSize: '17px',
                            fontWeight: '700',
                            cursor: selectingTierId ? 'not-allowed' : 'pointer',
                            transition: 'opacity 0.2s',
                            opacity: selectingTierId && selectingTierId !== tier.id ? 0.7 : 1,
                          }
                        : {
                            width: '100%',
                            padding: '16px',
                            borderRadius: '14px',
                            border: '1.5px solid var(--orange)',
                            backgroundColor: tier.isPopular ? 'var(--orange)' : 'var(--bx-surface)',
                            color: tier.isPopular ? 'var(--bx-surface)' : 'var(--orange)',
                            fontSize: '17px',
                            fontWeight: '700',
                            cursor: selectingTierId ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s',
                            opacity: selectingTierId && selectingTierId !== tier.id ? 0.7 : 1,
                          }
                    }
                    className="tier-select-btn"
                  >
                    {selectingTierId === tier.id
                      ? 'Preparing bundle...'
                      : tier.ctaLabel || 'Select Tier'}
                    {isB2BTab ? <span aria-hidden="true">→</span> : null}
                  </button>
                </div>
              </div>
            ))}

          {!loading && !error && activeTab === 'b2b' ? (
            <div
              style={{
                border: '1.5px solid #FED7AA',
                borderRadius: '18px',
                padding: '18px 20px',
                backgroundColor: 'var(--bx-brand-soft)',
                color: '#7C2D12',
                fontSize: '15px',
                lineHeight: '1.6',
              }}
            >
              <strong style={{ color: '#9A3412' }}>Discreet Delivery:</strong> All items delivered in plain,
              unlabelled bags by BOQBOX Queens. Your business remains professional and private.
            </div>
          ) : null}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      <BundlePreparationOverlay isOpen={selectingTierId !== null} />
    </div>
  );
}
